import React, { useEffect, useState } from 'react';
import { api } from '@/lib/api';
import { toast } from 'sonner';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Package, Plus, Search, AlertTriangle, PackageCheck, PackageX, HandCoins } from 'lucide-react';

const STATUS_META = {
  in_stock: { label: 'In Stock', color: '#059669', bg: '#D1FAE5' },
  reserved: { label: 'Reserved', color: '#0284C7', bg: '#DBEAFE' },
  allocated: { label: 'Allocated', color: '#B45309', bg: '#FEF3C7' },
  sold: { label: 'Sold', color: '#065F46', bg: '#A7F3D0' },
  returned: { label: 'Returned', color: '#7F1D1D', bg: '#FECACA' },
  damaged: { label: 'Damaged', color: '#DC2626', bg: '#FECACA' },
};

export default function InventoryTab() {
  const [kpi, setKpi] = useState(null);
  const [tab, setTab] = useState('summary');
  const [stock, setStock] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const [serials, setSerials] = useState([]);
  const [devices, setDevices] = useState([]);
  const [clinics, setClinics] = useState([]);
  const [clinicId, setClinicId] = useState('');
  const [status, setStatus] = useState('');
  const [q, setQ] = useState('');
  const [showAdd, setShowAdd] = useState(null);  // 'device' | 'serial' | null
  const [showAlloc, setShowAlloc] = useState(null); // serial obj | null

  const loadAll = async () => {
    try {
      const [k, st, al, dv, sr, cl] = await Promise.all([
        api.get('/inventory/kpi'),
        api.get('/inventory/stock-summary', { params: clinicId ? { clinic_id: clinicId } : {} }),
        api.get('/inventory/low-stock-alerts', { params: { threshold: 3 } }),
        api.get('/inventory/devices'),
        api.get('/inventory/serials', { params: { clinic_id: clinicId || undefined, status: status || undefined, q: q || undefined, limit: 300 } }),
        api.get('/clinics'),
      ]);
      setKpi(k.data); setStock(st.data.data); setAlerts(al.data.data);
      setDevices(dv.data.data); setSerials(sr.data.data); setClinics(cl.data);
    } catch { toast.error('Failed to load inventory'); }
  };
  useEffect(() => { loadAll(); }, [clinicId, status]);

  return (
    <div className="space-y-3" data-testid="admin-inventory-tab">
      {/* KPI header */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
        <KpiCard label="Devices (SKUs)" value={kpi?.total_devices} icon={Package} color="#0F766E" tid="inv-kpi-devices" />
        <KpiCard label="Total Serials" value={kpi?.total_serials} icon={Package} color="#0284C7" tid="inv-kpi-total" />
        <KpiCard label="In Stock" value={kpi?.in_stock} icon={PackageCheck} color="#059669" tid="inv-kpi-instock" />
        <KpiCard label="Allocated" value={kpi?.allocated} icon={HandCoins} color="#B45309" tid="inv-kpi-alloc" />
        <KpiCard label="Sold" value={kpi?.sold} icon={PackageX} color="#065F46" tid="inv-kpi-sold" />
      </div>

      {/* Tabs */}
      <div className="flex items-center gap-1 border-b border-stone-200">
        {['summary','serials','devices','alerts'].map(t => (
          <button key={t} onClick={() => setTab(t)} data-testid={`inv-subtab-${t}`}
            className={`px-3 py-1.5 text-xs font-semibold border-b-2 -mb-px transition-colors ${
              tab === t ? 'border-stone-900 text-stone-900' : 'border-transparent text-stone-500 hover:text-stone-700'
            }`}>{t[0].toUpperCase() + t.slice(1)}</button>
        ))}
        <div className="flex-1" />
        <Button size="sm" variant="outline" className="h-8 text-xs" onClick={() => setShowAdd('device')} data-testid="inv-add-device">
          <Plus className="w-3 h-3 mr-1" /> New Device
        </Button>
        <Button size="sm" className="h-8 text-xs" onClick={() => setShowAdd('serial')} data-testid="inv-add-serial">
          <Plus className="w-3 h-3 mr-1" /> New Serial
        </Button>
      </div>

      {/* filters */}
      <Card className="p-2 border-stone-200">
        <div className="flex items-center gap-2 flex-wrap">
          <Select value={clinicId || 'all'} onValueChange={(v) => setClinicId(v === 'all' ? '' : v)}>
            <SelectTrigger className="h-8 w-52 text-xs" data-testid="inv-filter-clinic">
              <SelectValue placeholder="All clinics" />
            </SelectTrigger>
            <SelectContent className="max-h-72">
              <SelectItem value="all">All Clinics</SelectItem>
              {clinics.map(c => (<SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>))}
            </SelectContent>
          </Select>
          <Select value={status || 'all'} onValueChange={(v) => setStatus(v === 'all' ? '' : v)}>
            <SelectTrigger className="h-8 w-40 text-xs" data-testid="inv-filter-status">
              <SelectValue placeholder="All statuses" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Statuses</SelectItem>
              {Object.entries(STATUS_META).map(([k, m]) => (<SelectItem key={k} value={k}>{m.label}</SelectItem>))}
            </SelectContent>
          </Select>
          <div className="relative">
            <Search className="w-3 h-3 absolute left-2 top-1/2 -translate-y-1/2 text-stone-400" />
            <Input value={q} onChange={(e) => setQ(e.target.value)} onBlur={loadAll} placeholder="Search serial…" className="h-8 pl-7 w-52 text-xs" data-testid="inv-filter-q" />
          </div>
          <div className="flex-1" />
          {alerts.length > 0 && (
            <div className="flex items-center gap-1 text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded px-2 py-1">
              <AlertTriangle className="w-3.5 h-3.5" /> {alerts.length} low-stock alert(s)
            </div>
          )}
        </div>
      </Card>

      {tab === 'summary' && (
        <Card className="p-0 border-stone-200 overflow-auto max-h-[560px] hc-scrollbar">
          <table className="w-full text-xs">
            <thead className="bg-stone-100 sticky top-0">
              <tr>
                {['Item','In Stock','Reserved','Allocated','Sold','Returned','Damaged','Total'].map(h => (
                  <th key={h} className="px-2 py-1.5 text-left font-semibold text-stone-700 border-b border-stone-200">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {stock.map(r => (
                <tr key={r.item_id} className="border-b border-stone-100 hover:bg-stone-50">
                  <td className="px-2 py-1.5 font-medium text-stone-900 truncate max-w-md">{r.item_name || r.item_id}</td>
                  <td className="px-2 py-1.5 tabular-nums text-emerald-700 font-semibold">{r.in_stock}</td>
                  <td className="px-2 py-1.5 tabular-nums text-blue-700">{r.reserved}</td>
                  <td className="px-2 py-1.5 tabular-nums text-amber-700">{r.allocated}</td>
                  <td className="px-2 py-1.5 tabular-nums text-stone-700">{r.sold}</td>
                  <td className="px-2 py-1.5 tabular-nums text-red-700">{r.returned}</td>
                  <td className="px-2 py-1.5 tabular-nums text-red-700">{r.damaged}</td>
                  <td className="px-2 py-1.5 tabular-nums font-bold">{r.total}</td>
                </tr>
              ))}
              {!stock.length && (
                <tr><td colSpan={8} className="px-4 py-8 text-center text-stone-400 text-xs">No serials tracked yet. Click &quot;New Serial&quot; to add stock.</td></tr>
              )}
            </tbody>
          </table>
        </Card>
      )}

      {tab === 'serials' && (
        <Card className="p-0 border-stone-200 overflow-auto max-h-[560px] hc-scrollbar">
          <table className="w-full text-xs">
            <thead className="bg-stone-100 sticky top-0">
              <tr>
                {['Serial No.','Device','Clinic','Status','Patient','Actions'].map(h => (
                  <th key={h} className="px-2 py-1.5 text-left font-semibold text-stone-700 border-b border-stone-200">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {serials.map(s => {
                const m = STATUS_META[s.status] || STATUS_META.in_stock;
                return (
                  <tr key={s.id} className="border-b border-stone-100 hover:bg-stone-50">
                    <td className="px-2 py-1.5 font-mono font-semibold text-stone-900">{s.serial_no}</td>
                    <td className="px-2 py-1.5 text-stone-700">{s.item_name || '—'}</td>
                    <td className="px-2 py-1.5 text-stone-700">{s.clinic_name || '—'}</td>
                    <td className="px-2 py-1.5">
                      <span className="text-[10px] px-1.5 py-0.5 rounded font-semibold" style={{ backgroundColor: m.bg, color: m.color }}>{m.label}</span>
                    </td>
                    <td className="px-2 py-1.5 text-stone-700">{s.patient_name || '—'}</td>
                    <td className="px-2 py-1.5">
                      <Button size="sm" variant="ghost" className="h-6 text-[10px]" onClick={() => setShowAlloc(s)} data-testid={`inv-allocate-${s.id}`}>
                        Allocate
                      </Button>
                    </td>
                  </tr>
                );
              })}
              {!serials.length && (
                <tr><td colSpan={6} className="px-4 py-8 text-center text-stone-400 text-xs">No serials match the filters.</td></tr>
              )}
            </tbody>
          </table>
        </Card>
      )}

      {tab === 'devices' && (
        <Card className="p-0 border-stone-200 overflow-auto max-h-[560px] hc-scrollbar">
          <table className="w-full text-xs">
            <thead className="bg-stone-100 sticky top-0">
              <tr>
                {['Name','Code','Brand','Category','MRP','Warranty','Active'].map(h => (
                  <th key={h} className="px-2 py-1.5 text-left font-semibold text-stone-700 border-b border-stone-200">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {devices.map(d => (
                <tr key={d.id} className="border-b border-stone-100 hover:bg-stone-50">
                  <td className="px-2 py-1.5 font-medium text-stone-900">{d.name}</td>
                  <td className="px-2 py-1.5 font-mono text-stone-600">{d.code || '—'}</td>
                  <td className="px-2 py-1.5 text-stone-700">{d.brand_name || '—'}</td>
                  <td className="px-2 py-1.5 text-stone-700">{d.category || '—'}</td>
                  <td className="px-2 py-1.5 tabular-nums text-emerald-700 font-semibold">{d.mrp ? `₹${Number(d.mrp).toLocaleString()}` : '—'}</td>
                  <td className="px-2 py-1.5 tabular-nums">{d.warranty_months ? `${d.warranty_months} mo` : '—'}</td>
                  <td className="px-2 py-1.5">{d.active !== false ? <span className="text-emerald-700">✓</span> : <span className="text-stone-400">—</span>}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      )}

      {tab === 'alerts' && (
        <Card className="p-3 border-stone-200">
          {alerts.length === 0 ? (
            <div className="text-center py-8 text-stone-400 text-xs">All clear — no low-stock alerts (threshold: 3 units).</div>
          ) : (
            <div className="space-y-1">
              {alerts.map(a => (
                <div key={`${a.item_id}-${a.clinic_id}`} className="flex items-center gap-2 p-2 border border-amber-200 bg-amber-50 rounded text-xs">
                  <AlertTriangle className="w-4 h-4 text-amber-700" />
                  <div className="flex-1">
                    <div className="font-semibold text-stone-900">{a.item_name}</div>
                    <div className="text-stone-600 text-[10px]">{a.clinic_name || 'Unassigned'}</div>
                  </div>
                  <div className="font-bold text-amber-800 tabular-nums">{a.count} units left</div>
                </div>
              ))}
            </div>
          )}
        </Card>
      )}

      <NewDeviceDialog open={showAdd === 'device'} onClose={() => { setShowAdd(null); loadAll(); }} />
      <NewSerialDialog open={showAdd === 'serial'} devices={devices} clinics={clinics} onClose={() => { setShowAdd(null); loadAll(); }} />
      <AllocateDialog serial={showAlloc} onClose={() => { setShowAlloc(null); loadAll(); }} />
    </div>
  );
}

function KpiCard({ label, value, icon: Icon, color, tid }) {
  return (
    <div className="rounded-lg border border-stone-200 bg-white p-2.5" data-testid={tid}>
      <div className="flex items-center gap-1.5">
        <Icon className="w-3.5 h-3.5" style={{ color }} />
        <div className="text-[10px] uppercase tracking-widest text-stone-500 font-semibold">{label}</div>
      </div>
      <div className="font-heading text-xl font-bold text-stone-900 tabular-nums mt-1">
        {value != null ? value.toLocaleString() : '—'}
      </div>
    </div>
  );
}

function NewDeviceDialog({ open, onClose }) {
  const [form, setForm] = useState({ name: '', code: '', brand_name: '', category: '', mrp: '', warranty_months: '' });
  const save = async () => {
    try {
      const payload = { ...form, mrp: form.mrp ? Number(form.mrp) : null, warranty_months: form.warranty_months ? Number(form.warranty_months) : null };
      await api.post('/inventory/devices', payload);
      toast.success('Device created'); setForm({ name: '', code: '', brand_name: '', category: '', mrp: '', warranty_months: '' });
      onClose();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };
  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-md" data-testid="inv-new-device-dialog">
        <DialogHeader><DialogTitle>New Device (SKU)</DialogTitle><DialogDescription className="text-xs">Create a new device master record. Name is required; other fields optional.</DialogDescription></DialogHeader>
        <div className="space-y-2">
          <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Device name (required)" data-testid="inv-new-device-name" />
          <Input value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value })} placeholder="Code / SKU" />
          <Input value={form.brand_name} onChange={(e) => setForm({ ...form, brand_name: e.target.value })} placeholder="Brand" />
          <Input value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} placeholder="Category (RIC / BTE / CIC…)" />
          <Input value={form.mrp} onChange={(e) => setForm({ ...form, mrp: e.target.value })} placeholder="MRP (INR)" type="number" />
          <Input value={form.warranty_months} onChange={(e) => setForm({ ...form, warranty_months: e.target.value })} placeholder="Warranty (months)" type="number" />
        </div>
        <DialogFooter><Button onClick={save} disabled={!form.name} data-testid="inv-new-device-save">Save</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function NewSerialDialog({ open, devices, clinics, onClose }) {
  const [form, setForm] = useState({ item_id: '', serial_no: '', clinic_id: '', purchase_price: '', notes: '' });
  const save = async () => {
    try {
      const dev = devices.find(d => d.id === form.item_id);
      const clin = clinics.find(c => c.id === form.clinic_id);
      const payload = {
        ...form,
        item_name: dev?.name,
        clinic_name: clin?.name,
        purchase_price: form.purchase_price ? Number(form.purchase_price) : null,
      };
      await api.post('/inventory/serials', payload);
      toast.success('Serial added to stock');
      setForm({ item_id: '', serial_no: '', clinic_id: '', purchase_price: '', notes: '' });
      onClose();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };
  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-md" data-testid="inv-new-serial-dialog">
        <DialogHeader><DialogTitle>Add Serial to Stock</DialogTitle><DialogDescription className="text-xs">Register a unique serial for an existing device at a clinic.</DialogDescription></DialogHeader>
        <div className="space-y-2">
          <Select value={form.item_id || undefined} onValueChange={(v) => setForm({ ...form, item_id: v })}>
            <SelectTrigger data-testid="inv-new-serial-item"><SelectValue placeholder="Device (required)" /></SelectTrigger>
            <SelectContent className="max-h-72">
              {devices.slice(0, 500).map(d => (<SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>))}
            </SelectContent>
          </Select>
          <Input value={form.serial_no} onChange={(e) => setForm({ ...form, serial_no: e.target.value })} placeholder="Serial number (required)" data-testid="inv-new-serial-no" />
          <Select value={form.clinic_id || undefined} onValueChange={(v) => setForm({ ...form, clinic_id: v })}>
            <SelectTrigger><SelectValue placeholder="Clinic" /></SelectTrigger>
            <SelectContent className="max-h-72">
              {clinics.map(c => (<SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>))}
            </SelectContent>
          </Select>
          <Input value={form.purchase_price} onChange={(e) => setForm({ ...form, purchase_price: e.target.value })} placeholder="Purchase price" type="number" />
          <Input value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder="Notes (optional)" />
        </div>
        <DialogFooter><Button onClick={save} disabled={!form.item_id || !form.serial_no} data-testid="inv-new-serial-save">Save</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function AllocateDialog({ serial, onClose }) {
  const [form, setForm] = useState({ patient_name: '', ticket_id: '', status: 'allocated' });
  useEffect(() => { setForm({ patient_name: '', ticket_id: '', status: 'allocated' }); }, [serial?.id]);
  if (!serial) return null;
  const save = async () => {
    try {
      await api.post(`/inventory/serials/${serial.id}/allocate`, form);
      toast.success(`Serial ${serial.serial_no} → ${form.status}`);
      onClose();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };
  return (
    <Dialog open={!!serial} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-md" data-testid="inv-allocate-dialog">
        <DialogHeader><DialogTitle>Allocate serial <span className="font-mono text-emerald-700">{serial.serial_no}</span></DialogTitle><DialogDescription className="text-xs">Assign or update this serial&apos;s status.</DialogDescription></DialogHeader>
        <div className="space-y-2">
          <div className="text-xs text-stone-500">Device: <b>{serial.item_name || '—'}</b> · Clinic: <b>{serial.clinic_name || '—'}</b></div>
          <Input value={form.patient_name} onChange={(e) => setForm({ ...form, patient_name: e.target.value })} placeholder="Patient name" />
          <Input value={form.ticket_id} onChange={(e) => setForm({ ...form, ticket_id: e.target.value })} placeholder="Ticket ID (optional)" />
          <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>
              {Object.entries(STATUS_META).filter(([k]) => k !== 'in_stock').map(([k, m]) => (<SelectItem key={k} value={k}>{m.label}</SelectItem>))}
            </SelectContent>
          </Select>
        </div>
        <DialogFooter><Button onClick={save} data-testid="inv-allocate-save">Update</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
